import { useEffect, useRef, useState } from 'react'
import { getDataSource } from '../utils/dataSource'
import { UTCTimestamp } from 'lightweight-charts'
import { normalizeTime } from '../utils/timeUtils'

export function useWebSocket(
  symbol: string,
  timeframe: string,
  onUpdate: (data: any) => void
) {
  const wsRef = useRef<WebSocket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const symbolRef = useRef(symbol)
  const timeframeRef = useRef(timeframe)
  const onUpdateRef = useRef(onUpdate)

  // Update refs when props change
  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])

  useEffect(() => {
    symbolRef.current = symbol
  }, [symbol])

  useEffect(() => {
    timeframeRef.current = timeframe
  }, [timeframe])

  // Convert chart timeframe format to Binance format
  const getBinanceTimeframe = (tf: string): string => {
    switch (tf) {
      case '1m':
        return '1m'
      case '5m':
        return '5m'
      case '15m':
        return '15m'
      case '1h':
        return '1h'
      case '4h':
        return '4h'
      case '1d':
        return '1d'
      case '1w':
        return '1w'
      case '1M':
        return '1M'
      default:
        return '1d' // fallback
    }
  }

  useEffect(() => {
    const dataSource = getDataSource(symbol)

    if (dataSource === 'binance') {
      // Cleanup existing connection if any
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }

      const binanceTimeframe = getBinanceTimeframe(timeframe)

      // Create new connection with the current timeframe
      const ws = new WebSocket(
        `wss://stream.binance.com:9443/ws/${symbol.toLowerCase()}@kline_${binanceTimeframe}`
      )

      ws.onopen = () => {
        setIsConnected(true)
        console.log(
          `WebSocket connected for ${symbol} with timeframe ${binanceTimeframe}`
        )
      }

      ws.onmessage = event => {
        try {
          const message = JSON.parse(event.data)
          if (message.e === 'kline' && symbolRef.current === symbol) {
            const kline = message.k
            const normalizedTimestamp = normalizeTime((kline.t / 1000) as UTCTimestamp, timeframeRef.current)
            onUpdateRef.current({
              time: normalizedTimestamp,
              open: parseFloat(kline.o),
              high: parseFloat(kline.h),
              low: parseFloat(kline.l),
              close: parseFloat(kline.c),
              volume: parseFloat(kline.v)
            })
          }
        } catch (error) {
          console.error('WebSocket message error:', error)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        // WebSocket error occurred - just update connection state
        // Error details are not reliably available in WebSocket error events
        setIsConnected(false)
      }

      ws.onclose = event => {
        console.log(
          `WebSocket closed for ${symbol}. Code: ${event.code}, Reason: ${event.reason}`
        )
        setIsConnected(false)
        wsRef.current = null
      }

      wsRef.current = ws
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [symbol, timeframe]) // Re-connect when symbol or timeframe changes

  return { isConnected }
}
